import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  CButton,
  CButtonGroup,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CInputGroup,
  CSpinner,
} from "@coreui/react";
import dayjs from "dayjs";

import { ColoredValue } from "@src/components/ColoredValue/ColoredValue";
import { CLOSED_POSITIONS_FETCH_CAP } from "@src/hooks/useClosedPositions";
import { PORTFOLIO_PRESETS, type PortfolioPreset } from "@src/hooks/usePortfolioUrlState";
import { getPositionPnlUsd } from "@src/utils/portfolioData";
import { isPositionTrackingEnded } from "@src/utils/positionStatus";
import type { ApiExecution, ApiPosition } from "@tradingdroid/frontends-types";
import { CDataTable } from "@tradingdroid/frontends-components";
import styles from "./ClosedPositionsList.module.scss";

interface Props {
  executions: ApiExecution[];
  positions: ApiPosition[];
  loading: boolean;
  hitCap: boolean;
  capitalBase: number;
  preset: PortfolioPreset | null;
  from: string;
  to: string;
  // The range ends at the current time and moves with it.
  toIsNow: boolean;
  executionId: number | null;
  onPresetChange: (preset: PortfolioPreset) => void;
  onRangeChange: (from: string, to: string | null) => void;
  onExecutionChange: (executionId: number | null) => void;
}

export const ClosedPositionsList: React.FC<Props> = ({
  executions,
  positions,
  loading,
  hitCap,
  capitalBase,
  preset,
  from,
  to,
  toIsNow,
  executionId,
  onPresetChange,
  onRangeChange,
  onExecutionChange,
}) => {
  const executionsById = useMemo(() => {
    const map = new Map<number, string>();
    for (const exec of executions) map.set(exec.id, exec.name);
    return map;
  }, [executions]);

  const totals = useMemo(() => {
    const count = positions.length;
    let pnlSum = 0;
    let pctSum = 0;
    let pctCount = 0;
    let weightedPctSum = 0;
    let collateralSum = 0;

    for (const p of positions) {
      const pnl = getPositionPnlUsd(p);
      if (pnl == null) continue;
      pnlSum += pnl;
      const collateral = p.collateral_in ?? 0;
      const pct = p.net_profit_pct;
      // Skip rows with no stored percentage (legacy/backfilled) so they don't
      // drag the mean toward breakeven or pad the denominator.
      if (pct != null) {
        pctSum += pct;
        pctCount += 1;
      }
      if (collateral > 0) {
        weightedPctSum += (pct ?? 0) * collateral;
        collateralSum += collateral;
      }
    }

    const weightedPct = collateralSum > 0 ? weightedPctSum / collateralSum : 0;
    const meanPct = pctCount > 0 ? pctSum / pctCount : 0;
    return { count, pnlSum, weightedPct, meanPct };
  }, [positions]);

  const returnOnCapitalPct = capitalBase > 0 ? (totals.pnlSum / capitalBase) * 100 : null;

  const handleExecutionSelect = (value: string) => {
    if (!value) onExecutionChange(null);
    else onExecutionChange(Number(value));
  };

  const columns = [
    {
      columnKey: "execution",
      columnLabel: "Execution",
      cellRender: (p: ApiPosition) => {
        const execName = p.execution_id ? executionsById.get(p.execution_id) : undefined;
        if (!p.execution_id) return <span className="text-body-secondary">-</span>;
        return (
          <span>
            <Link to={`/execution/${p.execution_id}`} className="text-decoration-none">
              #{p.execution_id}
            </Link>
            {execName && <span className="ms-1">{execName}</span>}
          </span>
        );
      },
    },
    {
      columnKey: "id",
      columnLabel: "Position",
      columnStyle: { width: "100px" },
      cellRender: (p: ApiPosition) => <span>#{p.id}</span>,
    },
    {
      columnKey: "start_ts",
      columnLabel: "Opened (UTC)",
      columnStyle: { width: "150px" },
      cellRender: (p: ApiPosition) => (
        <span className="text-body-secondary">
          {dayjs.utc(p.start_ts).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      columnKey: "end_ts",
      columnLabel: "Closed (UTC)",
      columnStyle: { width: "150px" },
      cellRender: (p: ApiPosition) => (
        <span className="text-body-secondary">
          {p.end_ts ? dayjs.utc(p.end_ts).format("YYYY-MM-DD HH:mm") : "-"}
        </span>
      ),
    },
    {
      columnKey: "pnl_pct",
      columnLabel: "PnL %",
      columnStyle: { width: "90px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (p: ApiPosition) =>
        isPositionTrackingEnded(p) ? (
          <span className={`ClosedPositionPnlUnknown ${styles.ClosedPositionPnlUnknown}`}>
            Unknown
          </span>
        ) : (
          <ColoredValue value={p.net_profit_pct ?? 0} threshold={0} type="percentage" />
        ),
    },
    {
      columnKey: "pnl_usd",
      columnLabel: "PnL $",
      columnStyle: { width: "110px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (p: ApiPosition) => {
        const pnl = getPositionPnlUsd(p);
        return pnl == null ? (
          <span className={`ClosedPositionPnlUnknown ${styles.ClosedPositionPnlUnknown}`}>
            Unknown
          </span>
        ) : (
          <ColoredValue value={pnl} threshold={0} type="currency" />
        );
      },
    },
  ];

  return (
    <div className="ClosedPositionsList">
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
        <div>
          <CFormLabel className="small mb-1">From (UTC)</CFormLabel>
          <CFormInput
            type="datetime-local"
            size="sm"
            value={from}
            max={to}
            onChange={(e) => onRangeChange(e.target.value, toIsNow ? null : to)}
          />
        </div>
        <div>
          <CFormLabel className="small mb-1">To (UTC)</CFormLabel>
          <CInputGroup size="sm" className="ClosedPositionsList__to">
            <CFormInput
              type="datetime-local"
              value={to}
              min={from}
              onChange={(e) => onRangeChange(from, e.target.value)}
            />
            <CButton
              className="ClosedPositionsList__toNow"
              color="outline-primary"
              active={toIsNow && preset === null}
              title="End the range at the current time and keep it moving"
              onClick={() => onRangeChange(from, null)}
            >
              Now
            </CButton>
          </CInputGroup>
        </div>
        <CButtonGroup size="sm">
          {PORTFOLIO_PRESETS.map((p) => (
            <CButton
              key={p}
              color="outline-primary"
              active={preset === p}
              onClick={() => onPresetChange(p)}
            >
              {p}
            </CButton>
          ))}
        </CButtonGroup>
        <div className="ClosedPositionsList__executionFilter">
          <CFormLabel className="small mb-1">Execution</CFormLabel>
          <CFormSelect
            size="sm"
            value={executionId ?? ""}
            onChange={(e) => handleExecutionSelect(e.target.value)}
          >
            <option value="">All Executions</option>
            {executions.map((exec) => (
              <option key={exec.id} value={exec.id}>
                #{exec.id} {exec.name}
              </option>
            ))}
          </CFormSelect>
        </div>
      </div>

      {loading ? (
        <div className="text-center p-4">
          <CSpinner size="sm" />
          <span className="ms-2">Loading positions...</span>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-end align-items-center gap-4 px-3 py-2 border-top border-bottom small">
            <span className="text-body-secondary">
              Positions: <strong className="text-body">{totals.count}</strong>
            </span>
            <span
              className="text-body-secondary"
              title="Average profit per trade, on each trade's own size"
            >
              Per trade (mean) %:{" "}
              <ColoredValue value={totals.meanPct} threshold={0} type="percentage" />
            </span>
            <span
              className="text-body-secondary"
              title="Average profit per trade, bigger trades counting more"
            >
              Per trade (weighted) %:{" "}
              <ColoredValue value={totals.weightedPct} threshold={0} type="percentage" />
            </span>
            <span
              className="ClosedPositionsList__returnOnCapital text-body-secondary"
              title="Total PnL $ divided by the capital of the executions alive in the range"
            >
              Return on capital %:{" "}
              {returnOnCapitalPct !== null ? (
                <>
                  <ColoredValue value={returnOnCapitalPct} threshold={0} type="percentage" />
                  <span className="ms-1">(of ${capitalBase.toFixed(0)})</span>
                </>
              ) : (
                <span>-</span>
              )}
            </span>
            <span className="text-body-secondary">
              Total PnL $: <ColoredValue value={totals.pnlSum} threshold={0} type="currency" />
            </span>
          </div>

          {hitCap && (
            <div className="text-warning small px-3 py-1">
              Showing the most recent {CLOSED_POSITIONS_FETCH_CAP} positions. Narrow the date range
              to see all results in the period.
            </div>
          )}

          <CDataTable
            className="ClosedPositionsTable"
            columns={columns}
            items={positions}
            striped
            small
            hover
            responsive
            getRowProps={(p) => ({ id: `closed-position-${p.id}` })}
            getCellProps={(p, column) => ({
              id: `closed-position-${p.id}-${column.columnKey}`,
            })}
          />

          {!loading && positions.length === 0 && (
            <div className="text-center text-body-secondary p-4">
              No closed positions in the selected range.
            </div>
          )}
        </>
      )}
    </div>
  );
};
